import React, { useState, useEffect } from 'react';
import { Users, CheckCircle2 } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Custom star — évite les bugs de react-simple-star-rating ─────────────────
function StarIcon({ filled, hovered, onClick, onEnter, onLeave, size = 28 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      onClick={onClick}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      style={{ cursor: 'pointer', flexShrink: 0 }}
    >
      <polygon
        points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
        fill={filled || hovered ? '#A855F7' : 'transparent'}
        stroke="#A855F7"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function BlogRating({ slug }) {
  const [data, setData]     = useState({ average: 0, count: 0 });
  const [hover, setHover]   = useState(0);   // 0 = aucun hover
  const [done, setDone]     = useState(false);
  const [userScore, setScore] = useState(null);
  const [loading, setLoading] = useState(false);

  const storageKey = `rating_${slug}`;

  useEffect(() => {
    fetch(`${API_BASE}/api/blog/ratings/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setData(d); })
      .catch(() => {});

    const saved = localStorage.getItem(storageKey);
    if (saved) { setScore(parseFloat(saved)); setDone(true); }
  }, [slug]);

  const handleRate = async (star) => {
    if (done || loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/blog/ratings/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ score: star }),
      });
      if (res.ok) {
        const d = await res.json();
        setData(d);
        setScore(star);
        setDone(true);
        localStorage.setItem(storageKey, String(star));
      }
    } catch {}
    setLoading(false);
  };

  const label = data.average >= 4.5 ? 'Excellent'
              : data.average >= 3.5 ? 'Très bon'
              : data.average >= 2.5 ? 'Bon'
              : data.average >= 1.5 ? 'Passable'
              : 'En attente de notes';

  const hoverLabel = hover > 0
    ? ['', 'Mauvais', 'Passable', 'Bon', 'Très bon', 'Excellent'][hover]
    : 'Cliquez pour noter (1 à 5 étoiles)';

  return (
    <div className="my-10 glass rounded-2xl p-6 border border-border/30">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">

        {/* Score global */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-center">
            <p className="text-4xl font-bold text-foreground">
              {data.count > 0 ? data.average.toFixed(1) : '—'}
            </p>
            {/* Étoiles affichage score — pas interactives */}
            <div className="flex flex-row gap-0.5 justify-center my-1">
              {[1,2,3,4,5].map(i => (
                <svg key={i} width="16" height="16" viewBox="0 0 24 24">
                  <polygon
                    points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"
                    fill={i <= Math.round(data.average) ? '#f59e0b' : 'transparent'}
                    stroke="#f59e0b"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
          <div className="h-12 w-px bg-border/40" />
          <div className="text-center">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Users className="w-4 h-4" />
              <span className="text-sm font-semibold text-foreground">{data.count}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {data.count <= 1 ? 'évaluation' : 'évaluations'}
            </p>
          </div>
        </div>

        {/* Zone de vote */}
        <div className="flex-1">
          {done ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-green-400">
                <CheckCircle2 className="w-5 h-5" />
                <span>Merci pour votre {userScore && `note de ${userScore}/5`} !</span>
              </div>
              <button
                onClick={() => { setDone(false); setScore(null); localStorage.removeItem(storageKey); }}
                className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors"
              >
                Modifier ma note
              </button>
            </div>
          ) : (
            <div>
              <p className="text-sm font-semibold text-foreground mb-3">
                Cet article vous a été utile ?
              </p>
              {/* Étoiles interactives en ligne */}
              <div
                className="flex flex-row items-center gap-1"
                onMouseLeave={() => setHover(0)}
              >
                {[1,2,3,4,5].map(star => (
                  <StarIcon
                    key={star}
                    filled={star <= (userScore || 0)}
                    hovered={star <= hover}
                    size={30}
                    onEnter={() => !done && setHover(star)}
                    onLeave={() => {}}
                    onClick={() => handleRate(star)}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {hoverLabel}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
